import React from "react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from "recharts";
import { Brain, MoonStar, Activity } from "lucide-react";

function Metric({ icon: Icon, label, value, unit, color, testid }) {
  return (
    <div className="bio-metric" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-400">
        <Icon size={13} style={{ color }} strokeWidth={1.8} />
        <span className="telem-label">{label}</span>
      </div>
      <div className="font-mono text-lg sm:text-xl font-bold text-white leading-none mt-1">
        {value}
        <span className="text-[10px] text-slate-500 ml-1 font-normal">{unit}</span>
      </div>
    </div>
  );
}

export default function BiometricSleepHUD({ sim, series }) {
  return (
    <motion.div
      data-testid="biometric-sleep-hud"
      initial={{ opacity: 0, x: -24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.7, delay: 0.15 }}
      className="hud-panel w-[300px] sm:w-[320px]"
    >
      <div className="hud-head">
        <span className="hud-kicker">Circalunar Biometrics</span>
        <span className="live-dot" />
      </div>
      <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">
        Pineal entrainment tracked against lunar luminance — {sim.phase}
      </p>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Metric icon={MoonStar} label="Melatonin" value={sim.melatonin.toFixed(0)} unit="pg/mL" color="#fb7185" testid="bio-melatonin" />
        <Metric icon={Brain} label="δ Delta" value={sim.delta.toFixed(0)} unit="%" color="#38bdf8" testid="bio-delta" />
        <Metric icon={Activity} label="Onset" value={`+${sim.sleepOnset.toFixed(0)}`} unit="min" color="#a78bfa" testid="bio-onset" />
      </div>

      <div className="h-[120px] -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="t" hide />
            <YAxis hide domain={[0, 100]} />
            <Tooltip
              contentStyle={{ background: "rgba(6,10,18,0.95)", border: "1px solid rgba(255,107,74,0.3)", borderRadius: 8, fontSize: 11 }}
              labelFormatter={(v) => series.find((d) => d.t === v)?.clock || ""}
            />
            <ReferenceLine x={sim.timeMin} stroke="#ff6b4a" strokeDasharray="3 3" />
            <Line type="monotone" dataKey="melatonin" stroke="#fb7185" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="delta" stroke="#38bdf8" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="onset" stroke="#a78bfa" strokeWidth={1.5} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-3 mt-1 text-[9px] text-slate-500">
        <span className="flex items-center gap-1"><i className="dot" style={{ background: "#fb7185" }} />Melatonin</span>
        <span className="flex items-center gap-1"><i className="dot" style={{ background: "#38bdf8" }} />Delta-wave</span>
        <span className="flex items-center gap-1"><i className="dot" style={{ background: "#a78bfa" }} />Sleep onset</span>
      </div>
    </motion.div>
  );
}
